/**
 * Turn-based game engine. Manages turn phases, income, end-of-turn,
 * victory conditions, and player elimination.
 */
import type { Building, PlayerId, TurnWorld, Unit, UnitType } from './types';
import { BUILDING_DEFS } from './buildings';
import { UNIT_DEFS } from './units';
import { getEffectiveMovement } from './supply';
import { getRecruitList } from './factions';
import { activeAurasFor, auraBonus } from './auras';
import { decrementCooldowns } from './abilities';

// --- Income phase ---

/** Add gold to current player based on controlled buildings. */
export function executeIncomePhase(world: TurnWorld): void {
  const player = currentPlayer(world);
  for (const b of world.buildings.values()) {
    if (b.owner === player.id) {
      const def = BUILDING_DEFS[b.type];
      player.gold += def.income;
    }
  }
  world.phase = 'action';
}

// --- Action phase helpers ---

/** Reset all units' acted flags and movement points for the current player at turn start. */
export function resetActedFlags(world: TurnWorld): void {
  const pid = currentPlayerId(world);
  for (const u of world.units.values()) {
    if (u.owner === pid) {
      u.acted = false;
      const baseMove = getEffectiveMovement(u);
      // Commander movement aura adds to mobile units (doesn't revive immobilised).
      const moveBonus = baseMove > 0 ? auraBonus(activeAurasFor(world, u), 'movementBonus') : 0;
      u.movePoints = baseMove + moveBonus;
      u.hasMoved = false;
      decrementCooldowns(u); // tick officer ability cooldowns
      // Also reset carried units so they can be unloaded this turn
      if (u.cargo) {
        for (const c of u.cargo) {
          if (c.owner === pid) c.acted = false;
        }
      }
    }
  }
}

// --- Recruitment ---

/** Start training a specific unit type at a building. Returns true if successful. */
export function startTraining(world: TurnWorld, building: Building, unitType: UnitType): boolean {
  if (building.training) return false;
  if (building.owner == null) return false;
  if (building.owner !== currentPlayerId(world)) return false;
  if (isTileOccupied(world, building.col, building.row)) return false;

  // Validate unit type against faction-aware recruit list (world+player so the
  // hero per-type limit is enforced — a second live hero can't be queued).
  const factionId = world.factions?.get(building.owner);
  const recruitList = getRecruitList(building.type, factionId, world, building.owner);
  if (!recruitList.includes(unitType)) return false;

  const player = world.players[building.owner];
  const unitDef = UNIT_DEFS[unitType];
  if (player.gold < unitDef.cost) return false;

  player.gold -= unitDef.cost;
  building.training = { unitType, turnsLeft: unitDef.trainingTime, totalTurns: unitDef.trainingTime };
  return true;
}

/**
 * Cancel training at a building. Returns the gold refunded.
 * Full refund if no turns have passed; 50% if training already started.
 */
export function cancelTraining(world: TurnWorld, building: Building): number {
  if (!building.training) return 0;
  if (building.owner == null) return 0;

  const unitDef = UNIT_DEFS[building.training.unitType];
  const elapsed = building.training.totalTurns - building.training.turnsLeft;
  const refund = elapsed > 0 ? Math.floor(unitDef.cost / 2) : unitDef.cost;

  const player = world.players[building.owner];
  player.gold += refund;
  building.training = null;
  return refund;
}

/**
 * Cancel training on any building that has an enemy unit standing on it.
 * No refund — the disruption is a penalty for leaving production undefended.
 */
export function cancelDisruptedTraining(world: TurnWorld): void {
  for (const b of world.buildings.values()) {
    if (!b.training) continue;
    for (const u of world.units.values()) {
      if (u.col === b.col && u.row === b.row && u.owner !== b.owner) {
        b.training = null;
        break;
      }
    }
  }
}

// --- End-turn phase ---

/** Advance training timers and spawn completed units. */
export function advanceTraining(world: TurnWorld): void {
  const pid = currentPlayerId(world);
  for (const b of world.buildings.values()) {
    if (b.owner !== pid || !b.training) continue;
    b.training.turnsLeft--;
    if (b.training.turnsLeft <= 0) {
      spawnUnit(world, b);
      b.training = null;
    }
  }
}

/** Spawn a unit on or adjacent to a building. */
function spawnUnit(world: TurnWorld, building: Building): void {
  const unitType = building.training!.unitType;
  const spawn = findSpawnTile(world, building.col, building.row);
  if (!spawn) return; // no valid spawn tile (shouldn't happen normally)

  const unitDef = UNIT_DEFS[unitType];
  const unit: Unit = {
    id: world.nextId++,
    type: unitType,
    owner: building.owner!,
    col: spawn.col,
    row: spawn.row,
    hp: unitDef.maxHp,
    acted: true, // newly spawned units can't act this turn
    movePoints: 0,
    fuel: unitDef.maxFuel,
    ammo: unitDef.maxAmmo,
    xp: 0,
    ...(unitDef.cargoCapacity ? { cargo: [] } : {}),
  };
  world.units.set(unit.id, unit);
}

/** Find an empty tile on or adjacent to (col, row). */
function findSpawnTile(world: TurnWorld, col: number, row: number): { col: number; row: number } | null {
  const candidates = [
    { col, row },
    { col: col + 1, row },
    { col: col - 1, row },
    { col, row: row + 1 },
    { col, row: row - 1 },
    { col: col + 1, row: row + 1 },
    { col: col - 1, row: row - 1 },
    { col: col + 1, row: row - 1 },
    { col: col - 1, row: row + 1 },
  ];
  for (const c of candidates) {
    if (c.col < 0 || c.row < 0 || c.col >= world.map.width || c.row >= world.map.height) continue;
    if (!isTileOccupied(world, c.col, c.row)) return c;
  }
  return null;
}

/** Check if any unit occupies a tile. */
export function isTileOccupied(world: TurnWorld, col: number, row: number): boolean {
  for (const u of world.units.values()) {
    if (u.col === col && u.row === row) return true;
  }
  return false;
}

// --- Victory / Elimination ---

/** Check victory condition: a player wins when they're the last one alive.
 *  Single-player scenarios (only 1 player defined) never trigger this. */
export function checkVictory(world: TurnWorld): PlayerId | null {
  if (world.players.length <= 1) return null; // single-player — no engine-level victory
  const livingPlayers = world.players.filter(p => p.alive);
  if (livingPlayers.length <= 1) {
    return livingPlayers[0]?.id ?? null;
  }
  return null;
}

/** Eliminate players who lost their HQ (HQ-based scenarios), or — in scenarios
 *  with no HQ buildings at all (e.g. pure naval battles) — players who have no
 *  units left. Also removes all their units. Single-player scenarios are exempt.
 *
 *  The no-HQ fallback is essential: without it, a buildingless scenario would
 *  flag *every* player as having "lost their HQ" on the first end-turn, killing
 *  all units and leaving the next-player advance loop with no living player to
 *  land on (an infinite loop). */
export function checkElimination(world: TurnWorld): void {
  if (world.players.length <= 1) return; // single-player — skip
  const anyHQ = [...world.buildings.values()].some(b => b.type === 'hq');
  for (const player of world.players) {
    if (!player.alive) continue;
    const eliminated = anyHQ
      ? ![...world.buildings.values()].some(b => b.type === 'hq' && b.owner === player.id)
      : ![...world.units.values()].some(u => u.owner === player.id);
    if (eliminated) {
      player.alive = false;
      // Remove all their units
      for (const [id, u] of world.units.entries()) {
        if (u.owner === player.id) world.units.delete(id);
      }
      // Release all their buildings to neutral
      for (const [, b] of world.buildings) {
        if (b.owner === player.id && b.type !== 'hq') {
          b.owner = null;
        }
      }
    }
  }
}

// --- Turn progression ---

/** Execute end-of-turn: advance timers, check elimination, advance to next player. */
export function executeEndTurn(world: TurnWorld): PlayerId | null {
  advanceTraining(world);
  checkElimination(world);

  const winner = checkVictory(world);
  if (winner != null) return winner;

  // Advance to next living player. Bound the scan to the player count so a
  // state with no living players (e.g. mutual annihilation) can never spin
  // forever — it just stops on the next index.
  for (let step = 0; step < world.turnOrder.length; step++) {
    world.currentPlayerIndex = (world.currentPlayerIndex + 1) % world.turnOrder.length;
    if (world.players[world.turnOrder[world.currentPlayerIndex]].alive) break;
  }

  // If we've looped back to the first player, increment turn number
  if (world.currentPlayerIndex === 0) {
    world.turnNumber++;
  }

  // Start new turn for next player
  world.phase = 'income';
  resetActedFlags(world);
  executeIncomePhase(world);

  return null; // no winner yet
}

/** Begin the very first turn of the game. */
export function startGame(world: TurnWorld): void {
  world.phase = 'income';
  world.turnNumber = 1;
  world.currentPlayerIndex = 0;
  resetActedFlags(world);
  executeIncomePhase(world);
}

// --- Helpers ---

export function currentPlayerId(world: TurnWorld): PlayerId {
  return world.turnOrder[world.currentPlayerIndex];
}

export function currentPlayer(world: TurnWorld) {
  return world.players[currentPlayerId(world)];
}
